import React, { useEffect, useState } from "react";
import { Form, FormGroup, Input, Row, Col } from "reactstrap";
import HusariaSelect from "components/HusariaSelect.js";
import Tooltip from "components/ToolTip.js";
import { serverHost } from "variables/serverdata.js";
/**
 * DomainStep — wizard step for domain configuration and module assignment.
 *
 * Props:
 *   formData        — full wizard formData; reads from formData.domain slice:
 *                     {
 *                       useExisting: boolean,
 *                       domain_uuid: string,   // when useExisting = true
 *                       domain_name: string,   // when useExisting = false (blank = auto-generate)
 *                       modules: string[],     // selected module ids
 *                     }
 *   updateSection   — (section, field, value) => void
 *   existingDomains — [{ domain_uuid, domain_name }] fetched from the server
 *   availableModules — [{ module_uuid, module_name }] fetched from the server
 */

function generateCodes(companyName) {
  // Clean and uppercase the name
  const clean = companyName.toUpperCase().replace(/[^A-Z0-9]/g, "");

  const codes = new Set();

  // Strategy 1: First 4 chars
  if (clean.length >= 4) codes.add(clean.slice(0, 4));

  // Strategy 2: First + last chars (e.g. ACME -> ACME, MICROSOFT -> MSFT-like)
  if (clean.length >= 4)
    codes.add(
      clean[0] + clean[1] + clean[clean.length - 2] + clean[clean.length - 1],
    );

  // Strategy 3: Every nth character spread across the string
  if (clean.length >= 4) {
    const step = Math.floor(clean.length / 4);
    codes.add([0, 1, 2, 3].map((i) => clean[i * step]).join(""));
  }

  // Strategy 4: First char + consonants (up to 3)
  const consonants = clean.slice(1).replace(/[AEIOU]/g, "");
  if (consonants.length >= 3) codes.add(clean[0] + consonants.slice(0, 3));

  // Pad or fill remaining with numeric suffixes if we don't have 4 unique ones
  const base = clean.slice(0, 3).padEnd(3, "X");
  let i = 1;
  while (codes.size < 4) {
    codes.add((base + i).slice(0, 4));
    i++;
  }

  return [...codes].slice(0, 4);
}

function DomainStep({ formData = {}, updateSection, availableModules = [], domainExists, setDomainExists }) {
  const domain = formData.domain || {};
  const cmpy_codes = generateCodes(formData.site_information.company_name);
  const [existingDomains, setExistingDomains] = useState([]);
  console.log(availableModules);

  const fetch_domains = async () => {
    try {
      const resp = await fetch(`${serverHost}/domain_codes`);
      const data = await resp.json();
      if (!resp.ok) {
        throw Error("Failed to get domain list: ", data.message);
      }
      console.log(data.data);
      setExistingDomains(data.data);
    } catch {}
  };

  const handleChange = (e) => {
    const { id, value } = e.target;
    const field = id.replace("input-", "");
    updateSection("domain", field, value);
  };

  const handleModuleToggle = (moduleId) => {
    const current = domain.modules || [];
    const updated = current.includes(moduleId)
      ? current.filter((id) => id !== moduleId)
      : [...current, moduleId];
    updateSection("domain", "modules", updated);
  };

  const selectedModules = domain.modules || [];
  useEffect(() => {
    fetch_domains();
  }, []);
  return (
    <div className="pl-lg-4">
      {/* Domain field — switches between select and text input */}
      <Row>
        <Col lg="6">
          <FormGroup>
            <label className="form-control-label" htmlFor="input-domain_name">
              Domain Name
            </label>
            <Tooltip text="Domain name used to access the site.">
              <label
                style={{
                  marginLeft: "10px",
                  fontSize: "12px",
                  fontWeight: "bold",
                  paddingBottom: "5px",
                  cursor: "pointer",
                }}
              >
                ?
              </label>
            </Tooltip>
            <Input
              id="input-domain_name"
              value={formData.domain_detail.domain_name || ""}
              onChange={(e) => {
                const val = e.target.value.slice(0, 4);
                updateSection("domain_detail", "domain_name", val);
                setDomainExists(existingDomains.includes(val));
              }}
              placeholder="e.g. acbd"
              maxLength={4}
              type="text"
              isInvalid={domainExists}
            />
            {domainExists && (
              <small
                style={{
                  color: "#dc3545",
                  display: "block",
                  marginTop: "0.25rem",
                }}
              >
                This domain name already exists.
              </small>
            )}
            <small className="text-muted">
              Leave this blank to auto generate a domain. Suggestions:{" "}
              {cmpy_codes.map((code, i) => (
                <span
                  key={code}
                  style={{ cursor: "pointer", textDecoration: "underline" }}
                  onClick={() =>
                    updateSection("domain_detail", "domain_name", code)
                  }
                >
                  {code}
                  {i < cmpy_codes.length - 1 ? ", " : ""}
                </span>
              ))}
            </small>
          </FormGroup>
        </Col>

        {/* Modules */}

        <Col lg="6">
          <div className="d-flex align-items-center mb-2">
            <label className="form-control-label d-block mb-2">Modules</label>
            <Tooltip text="Modules the site will have access to.">
              <label
                style={{
                  marginLeft: "10px",
                  fontSize: "12px",
                  fontWeight: "bold",
                  paddingBottom: "5px",
                  cursor: "pointer",
                }}
              >
                ?
              </label>
            </Tooltip>
          </div>
          {availableModules.length === 0 ? (
            <p className="text-muted" style={{ fontSize: "0.875rem" }}>
              No modules available.
            </p>
          ) : (
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fill, minmax(180px, 1fr))",
                gap: "10px",
              }}
            >
              <HusariaSelect
                isMulti
                name="module_perms"
                id="module_perms"
                options={availableModules.map((m) => ({
                  value: m.name,
                  label: m.name,
                }))}
                value={(formData.domain_detail?.modules || [])
                  .map((name) => {
                    const module = availableModules.find(
                      (m) => m.name === name,
                    );
                    return module
                      ? { value: module.name, label: module.name }
                      : null;
                  })
                  .filter(Boolean)}
                onChange={(selectedOptions) => {
                  const selectedNames = selectedOptions.map((opt) => opt.value);
                  updateSection("domain_detail", "modules", selectedNames);
                }}
                style={{ height: "55px", width: "100%" }}
              />
            </div>
          )}
          {selectedModules.length > 0 && (
            <small className="text-muted d-block mt-2">
              {selectedModules.length} module
              {selectedModules.length !== 1 ? "s" : ""} selected
            </small>
          )}
        </Col>
      </Row>
    </div>
  );
}

export default DomainStep;
